import { isRequestError } from "../../request/errors/requestError.error";
import { MAX_IMAGE_UPLOAD_BYTES } from "./imageUploadLimits.constants";

/**
 * Why an image upload failed, narrowed to a single reason so callers can show
 * specific copy instead of a generic "please try again".
 */
export type ImageUploadFailureReason =
  | "network"
  | "permission"
  | "session_expired"
  | "too_large"
  | "unsupported_type"
  | "unknown";

export type PresignedUploadErrorDetails = {
  /** HTTP status from S3. Undefined when the request never completed. */
  status?: number;
  /** `<Code>` from S3's XML error document, e.g. `EntityTooLarge`. */
  s3Code?: string;
  /** `<Message>` from S3's XML error document. */
  s3Message?: string;
  /** `<MaxSizeAllowed>` — the cap S3 enforced, present on `EntityTooLarge`. */
  maxSizeAllowed?: number;
  /** `<ProposedSize>` — what we tried to upload, present on `EntityTooLarge`. */
  proposedSize?: number;
};

/**
 * Raised when the POST to the S3 presigned URL fails, carrying enough detail to
 * tell a rejected policy condition apart from a blocked or offline request.
 */
export class PresignedUploadError extends Error {
  status?: number;
  s3Code?: string;
  s3Message?: string;
  maxSizeAllowed?: number;
  proposedSize?: number;

  constructor(message: string, details: PresignedUploadErrorDetails = {}) {
    super(message);
    this.name = "PresignedUploadError";
    this.status = details.status;
    this.s3Code = details.s3Code;
    this.s3Message = details.s3Message;
    this.maxSizeAllowed = details.maxSizeAllowed;
    this.proposedSize = details.proposedSize;
  }
}

export const isPresignedUploadError = (e: unknown): e is PresignedUploadError =>
  e instanceof PresignedUploadError;

/** S3 codes returned when the object exceeds the policy's content-length-range. */
const S3_TOO_LARGE_CODES = ["EntityTooLarge", "MaxPostPreDataLengthExceeded"];

/**
 * S3 reports most policy-condition failures as a generic `AccessDenied`, so the
 * message body is the only way to tell which condition was violated.
 */
const violatedCondition = (s3Message: string | undefined, condition: string) =>
  s3Message?.toLowerCase().includes(condition.toLowerCase()) ?? false;

export const classifyImageUploadFailure = (
  error: unknown,
): ImageUploadFailureReason => {
  if (isPresignedUploadError(error)) {
    const { status, s3Code, s3Message } = error;

    if (
      (s3Code && S3_TOO_LARGE_CODES.includes(s3Code)) ||
      violatedCondition(s3Message, "content-length-range") ||
      violatedCondition(s3Message, "exceeds the maximum allowed size")
    )
      return "too_large";

    if (violatedCondition(s3Message, "$Content-Type"))
      return "unsupported_type";

    // No status means fetch() rejected, so the request never reached S3.
    if (status === undefined) return "network";
    if (status === 403) return "permission";

    return "unknown";
  }

  // Thrown by openApiRequest when the presigned-url endpoint itself fails.
  if (isRequestError(error)) {
    if (error.status === 401) return "session_expired";
    if (error.status === 403) return "permission";
    return "unknown";
  }

  // openApiRequest calls fetch() directly, which rejects with a TypeError when
  // the request never completed — offline, DNS/CORS failure, or an extension
  // blocking the host.
  if (error instanceof TypeError) return "network";

  return "unknown";
};

/**
 * The cap S3 actually enforced, preferred over our local constant so the copy
 * can't drift from the backend's presigned POST policy. S3 exposes it two ways
 * depending on how it reports the violation, and neither is guaranteed.
 */
export const resolveMaxUploadBytes = (error: unknown): number => {
  if (!isPresignedUploadError(error)) return MAX_IMAGE_UPLOAD_BYTES;

  // `EntityTooLarge` carries the limit as its own element...
  if (error.maxSizeAllowed !== undefined) return error.maxSizeAllowed;

  // ...but the same violation reported as `AccessDenied` only names it inside
  // the policy-condition message, e.g.
  // `Policy Condition failed: ["content-length-range", 0, 10485760]`.
  const fromCondition = /content-length-range"?\s*,\s*\d+\s*,\s*(\d+)/.exec(
    error.s3Message ?? "",
  )?.[1];

  return fromCondition ? Number(fromCondition) : MAX_IMAGE_UPLOAD_BYTES;
};

/** Single-line summary for logs — never shown to the user. */
export const describeImageUploadFailure = (error: unknown): string => {
  if (isPresignedUploadError(error)) return error.message;
  if (isRequestError(error))
    return `${error.status}${error.code ? ` (code ${error.code})` : ""}: ${error.message}`;
  if (error instanceof Error) return error.message;
  return String(error);
};
